/**
 * Uma caixa de texto que sugere quem citar quando você digita {@code @}.
 *
 * <p>É um componente, e não um punhado de props na {@code <textarea>}, porque as duas
 * caixas de comentário — a de escrever e a de editar — precisam do mesmo gesto, e o
 * gesto tem estado próprio: o que está sendo digitado depois do arroba, qual item está
 * destacado, e as teclas que a lista consome antes de o resto do teclado ver.
 */
import { useRef } from 'react';

import type { Membro } from '../api/tipos';
import { useSugestaoDeMencao } from './useSugestaoDeMencao';
import { Avatar } from '../comum/Avatar';
import './mencao.css';


export function CaixaComMencao({
  valor,
  aoMudar,
  membros,
  aoTeclar,
  aoSairDoCampo,
  ...resto
}: {
  valor: string;
  aoMudar(texto: string): void;
  membros: Membro[];
  /** As teclas que sobram depois que a lista pega as dela. */
  aoTeclar?(e: React.KeyboardEvent<HTMLTextAreaElement>): void;
  /**
   * O que fazer quando o campo perde o foco — salvar, na Descrição.
   *
   * <p>Tem nome próprio, e não vai por `onBlur` no `...resto`, justamente porque o
   * `onBlur` do `...resto` era engolido pelo deste componente. Um nome que não colide
   * não pode ser sobrescrito por engano; `onBlur` fica de fora do tipo pelo mesmo motivo
   * que `onChange` e `onKeyDown` já ficavam.
   */
  aoSairDoCampo?(e: React.FocusEvent<HTMLTextAreaElement>): void;
} & Omit<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  'value' | 'onChange' | 'onKeyDown' | 'onBlur'
>) {
  const campo = useRef<HTMLTextAreaElement>(null);
  const sugestao = useSugestaoDeMencao(membros);

  function aplicar(handle: string) {
    const { texto, cursor } = sugestao.escolher(valor, handle);
    aoMudar(texto);
    // Depois do render, senão o cursor volta para o fim do texto antigo.
    requestAnimationFrame(() => {
      campo.current?.focus();
      campo.current?.setSelectionRange(cursor, cursor);
    });
  }

  return (
    <div className="mn-caixa">
      <textarea
        {...resto}
        ref={campo}
        value={valor}
        onChange={(e) => {
          aoMudar(e.target.value);
          sugestao.aoDigitar(e.target);
        }}
        onKeyDown={(e) => {
          const escolhido = sugestao.aoTeclar(e);
          if (escolhido) return aplicar(escolhido);
          // null é "a lista tratou e não escolheu ninguém" — seta, Esc. Nesses casos o
          // resto do teclado não pode ver a tecla, ou Esc fecharia o modal inteiro.
          if (escolhido === null) return;
          aoTeclar?.(e);
        }}
        /*
         * O blur faz DUAS coisas, e este componente só fazia a dele.
         *
         * O `{...resto}` acima espalha as props de quem chama, e o `onBlur` declarado
         * aqui embaixo vinha depois — em JSX, o último vence. Resultado: o `onBlur` que
         * a Descrição passava para SALVAR era silenciosamente descartado, e o texto
         * digitado sumia ao clicar fora. Foi assim que a descrição "não ficava salva".
         *
         * Agora os dois rodam: o de fora primeiro, o de fechar a lista depois. O atraso
         * continua sendo do fechamento — sem ele, o blur mata a lista antes de o clique
         * nela chegar, e escolher com o mouse nunca funcionaria.
         */
        onBlur={(e) => {
          aoSairDoCampo?.(e);
          setTimeout(sugestao.fechar, 150);
        }}
      />

      {sugestao.aberta && (
        <ul className="mn-lista" role="listbox">
          {sugestao.candidatos.map((m, i) => (
            <li key={m.id}>
              <button
                type="button"
                role="option"
                aria-selected={i === sugestao.destacado}
                className={i === sugestao.destacado ? 'on' : ''}
                onMouseEnter={() => sugestao.setDestacado(i)}
                onClick={() => aplicar(m.handle)}
              >
                <Avatar className="mn-av" nome={m.nome} cor={m.cor} url={m.avatar} />
                <span className="mn-nome">{m.nome}</span>
                <span className="mn-handle">@{m.handle}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
